import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';

const yuan = cents => (Number(cents || 0) / 100).toFixed(2);

function parseEvidence(value) {
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function Disputes() {
  const { copy } = useAdminLanguage();
  const disputesCopy = copy.disputes;
  const STATUS_LABEL = {
    open: disputesCopy.status.open,
    refunded: disputesCopy.status.refunded,
    released: disputesCopy.status.released,
    closed: disputesCopy.status.closed,
  };
  const STATUS_OPTIONS = [
    { value: 'open', label: disputesCopy.status.open },
    { value: 'refunded', label: disputesCopy.status.refunded },
    { value: 'released', label: disputesCopy.status.released },
    { value: 'closed', label: disputesCopy.status.closed },
    { value: 'all', label: disputesCopy.status.all },
  ];
  const RESOLUTION_LABEL = {
    refund: disputesCopy.resolution.refund,
    release: disputesCopy.resolution.release,
    close: disputesCopy.resolution.close,
  };
  const [status, setStatus] = useState('open');
  const [disputes, setDisputes] = useState([]);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('success');

  const load = useCallback(() => {
    setLoading(true);
    setMessage('');
    setMessageTone('success');
    api.get('/admin/finance/disputes', { params: { status } })
      .then(res => setDisputes(res.data.disputes || []))
      .catch(err => {
        setMessage(err.response?.data?.error || disputesCopy.errors.loadFailed);
        setMessageTone('error');
      })
      .finally(() => setLoading(false));
  }, [status, disputesCopy.errors.loadFailed]);

  useEffect(() => { load(); }, [load]);

  const totalAmount = useMemo(
    () => disputes.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [disputes]
  );

  const resolveDispute = async (item, resolution) => {
    const note = String(notes[item.id] || '').trim();
    if (!note) {
      setMessage(disputesCopy.noteRequired(item.id));
      setMessageTone('error');
      return;
    }
    setMessage('');
    setMessageTone('success');
    try {
      await api.post(`/admin/finance/disputes/${item.id}/resolve`, { resolution, note });
      setNotes(current => ({ ...current, [item.id]: '' }));
      await load();
      setMessage(disputesCopy.resolvedMessage(item.id, RESOLUTION_LABEL[resolution]));
      setMessageTone('success');
    } catch (err) {
      setMessage(err.response?.data?.error || disputesCopy.errors.resolveFailed);
      setMessageTone('error');
    }
  };

  return (
    <div>
      <div className="page-title">{disputesCopy.title}</div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {STATUS_OPTIONS.map(option => (
            <button
              key={option.value}
              className={`btn btn-sm ${status === option.value ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setStatus(option.value)}
            >
              {option.label}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            {disputesCopy.summary(disputes.length, yuan(totalAmount))}
          </span>
        </div>
      </div>

      {message && (
        <div className="card" style={{ color: messageTone === 'error' ? '#b91c1c' : '#166534', marginBottom: 16 }}>
          {message}
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{disputesCopy.columns.id}</th>
                <th>{disputesCopy.columns.order}</th>
                <th>{disputesCopy.columns.parties}</th>
                <th>{disputesCopy.columns.amount}</th>
                <th>{disputesCopy.columns.reason}</th>
                <th>{disputesCopy.columns.evidence}</th>
                <th>{disputesCopy.columns.status}</th>
                <th>{disputesCopy.columns.handling}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{disputesCopy.loading}</td></tr>
              ) : disputes.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{disputesCopy.empty}</td></tr>
              ) : disputes.map(item => {
                const evidence = parseEvidence(item.evidence);
                return (
                  <tr key={item.id}>
                    <td style={{ color: '#9ca3af' }}>#{item.id}</td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{item.order_no || `#${item.order_id}`}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 180 }}>
                        {item.task_title || disputesCopy.taskFallback(item.task_id)}
                      </div>
                    </td>
                    <td>
                      <div>{disputesCopy.buyerLabel}: {item.buyer_name || disputesCopy.emptyValue}</div>
                      <div>{disputesCopy.sellerLabel}: {item.seller_name || disputesCopy.emptyValue}</div>
                    </td>
                    <td style={{ color: '#1dbf73', fontWeight: 700 }}>¥ {yuan(item.amount)}</td>
                    <td style={{ maxWidth: 280 }}>
                      <div style={{ fontWeight: 600 }}>{item.reason}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{item.detail || disputesCopy.emptyValue}</div>
                    </td>
                    <td style={{ maxWidth: 220 }}>
                      {evidence.length ? evidence.map((entry, index) => (
                        <div key={`${item.id}-${index}`} style={{ fontSize: 12, wordBreak: 'break-all' }}>{entry}</div>
                      )) : disputesCopy.emptyValue}
                    </td>
                    <td>
                      <span className="badge badge-blue">{STATUS_LABEL[item.status] || item.status}</span>
                    </td>
                    <td style={{ minWidth: 260 }}>
                      {item.status === 'open' ? (
                        <div style={{ display: 'grid', gap: 8 }}>
                          <textarea
                            rows={2}
                            placeholder={disputesCopy.notePlaceholder}
                            value={notes[item.id] || ''}
                            onChange={event => setNotes(current => ({ ...current, [item.id]: event.target.value }))}
                            style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, resize: 'vertical' }}
                          />
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button className="btn btn-sm btn-outline" style={{ color: '#ef4444', borderColor: '#fecaca' }} onClick={() => resolveDispute(item, 'refund')}>
                              {disputesCopy.resolution.refund}
                            </button>
                            <button className="btn btn-sm btn-primary" onClick={() => resolveDispute(item, 'release')}>
                              {disputesCopy.resolution.release}
                            </button>
                            <button className="btn btn-sm btn-outline" onClick={() => resolveDispute(item, 'close')}>
                              {disputesCopy.resolution.close}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          {item.resolution_note || disputesCopy.emptyValue}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
